<div class="rdc-wrapper">
  <!-- Navigazione -->
  @if (showToolbar()) {
    <siat-calendar-toolbar
      [periodLabel]="formattedDate() | titlecase"
      [showAddButton]="splitButtonOptions().length > 0"
      [addButtonOptions]="splitButtonOptions()"
      (navigate)="navigate($event)"
      (addNew)="splitButtonAction.emit($event)"
      (dateSelected)="jumpToDate($event)"
    />
    @if (extraHeaderTemplate()) {
      <ng-container *ngTemplateOutlet="extraHeaderTemplate()" />
    }
  }

  <div class="flex items-stretch gap-3">
    <siat-parking-shelf
      [parkedEvents]="parkedEvents()"
      (dropped)="onParkingDrop($event)"
      (parkedDragStart)="onParkedEventDragStart($event.dragEvent, $event.parked)"
      (removeParked)="clearParkedEvent($event)"
    />

    <button
      mat-stroked-button
      class="rdc-add-col-btn"
      [disabled]="!canAddColumn()"
      matTooltip="Aggiungi un altro operatore alla vista"
      [attr.aria-label]="
        canAddColumn()
          ? 'Aggiungi operatore'
          : 'Numero massimo di colonne raggiunto'
      "
      (click)="addColumn()"
    >
      <mat-icon aria-hidden="true">person_add</mat-icon>
      Aggiungi operatore
    </button>
  </div>

  <!-- Griglia risorsa -->
  <div class="rdc-grid" [class.rdc-grid--weekend]="isWeekend()">
    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="rdc-header-row">
      <div class="rdc-time-cell rdc-header-time">ora</div>

      <!-- Header per ogni colonna -->
      @for (i of columnIndices(); track i) {
        <div
          class="rdc-resource-header"
          [class.rdc-resource-header--ambulatorio]="isAmbulatorioColumn(i)"
          [class.rdc-resource-header--sanitario]="!isAmbulatorioColumn(i)"
        >
          <div class="flex items-center justify-center gap-2 w-full">
            <mat-icon
              class="rdc-resource-header-icon"
              aria-hidden="true"
              [matTooltip]="
                isAmbulatorioColumn(i) ? 'Ambulatorio' : 'Sanitario'
              "
            >
              {{ isAmbulatorioColumn(i) ? 'meeting_room' : 'person' }}
            </mat-icon>
            <siat-select-field
              class="rdc-col-select-field"
              [formGroup]="columnFormGroups[i]"
              controlName="operatore"
              [items]="columnOptionsList()[i]"
              [label]="'Operatore ' + (i + 1)"
              [showLabel]="false"
              [ariaLabel]="'Operatore ' + (i + 1)"
              [showClearButton]="false"
              placeholder="Seleziona operatore"
            />
            @if (canRemoveColumn()) {
              <button
                mat-icon-button
                color="warn"
                class="rdc-remove-btn"
                aria-label="Rimuovi colonna operatore"
                matTooltip="Rimuovi colonna operatore"
                (click)="removeColumn(i)"
              >
                <mat-icon aria-hidden="true">delete_outline</mat-icon>
              </button>
            }
          </div>
          @if (columnHasMachinery(i)) {
            <div class="flex items-center gap-2 w-full mt-1.5">
              <mat-icon
                class="rdc-resource-header-icon"
                aria-hidden="true"
                matTooltip="Macchinario"
              >
                precision_manufacturing
              </mat-icon>
              <siat-select-field
                class="rdc-col-select-field"
                [formGroup]="columnMachineryFormGroups[i]"
                controlName="macchinario"
                [items]="columnMachineryOptionsList()[i]"
                [label]="'Macchinario ' + (i + 1)"
                [showLabel]="false"
                [ariaLabel]="'Macchinario colonna ' + (i + 1)"
                [showClearButton]="false"
                placeholder="Seleziona macchinario"
              />
            </div>
          }
        </div>
      }
    </div>

    <!-- ── Body: asse ore + colonne ──────────────────────────── -->
    <div class="rdc-body" #rdcBody [style.height.px]="gridHeight">
      <!-- Asse ore -->
      <div class="rdc-time-axis">
        @for (slot of timeSlots; track $index) {
          <div
            class="rdc-time-slot"
            [class.rdc-time-slot--hour]="slot.isHour"
            [style.height.px]="slotHeight"
          >
            @if (slot.isHour) {
              <span>{{ slot.label }}</span>
            }
          </div>
        }
      </div>

      <!-- Colonne risorse -->
      @let ds = dragState();
      @for (i of columnIndices(); track i) {
        <div
          class="rdc-col"
          [class.rdc-col--drag-over]="
            ds?.columnIndex === i && ds?.isValid && getColumnSelection(i)
          "
          [class.rdc-col--drag-invalid]="
            ds?.columnIndex === i &&
            ds?.isValid === false &&
            getColumnSelection(i)
          "
          [class.rdc-col--holiday]="showHolidays() && isCurrentDayHoliday()"
          (dragover)="onColumnDragOver($event, i)"
          (drop)="onColumnDrop($event, i)"
          (dragleave)="onColumnDragLeave($event, i)"
        >
          <!-- Linee griglia sfondo -->
          @for (slot of timeSlots; track $index) {
            @let hasColumn = !!getColumnSelection(i);
            @let slotClickable = slotClickEnabled() && hasColumn;
            @let slotAvailable = isSlotAvailable(i, $index);
            @let hasMachinery = columnHasMachinery(i);
            @let withinMachineryHours = isSlotWithinMachineryHours(i, $index);
            <div
              class="rdc-slot-bg"
              [class.rdc-slot-bg--hour]="slot.isHour"
              [class.rdc-slot-bg--clickable]="slotClickable && slotAvailable"
              [class.rdc-slot-bg--not-allowed]="slotClickable && !slotAvailable"
              [class.rdc-slot-bg--machinery-available]="
                hasMachinery && withinMachineryHours
              "
              [class.rdc-slot-bg--machinery-unavailable]="
                hasMachinery && !withinMachineryHours
              "
              [style.height.px]="slotHeight"
              [tabindex]="slotClickable && slotAvailable ? 0 : -1"
              [attr.role]="slotClickable && slotAvailable ? 'button' : null"
              (click)="onSlotClick(i, $index, $event)"
              (keyup.enter)="onSlotClick(i, $index, $event)"
            ></div>
          }

          <!-- Indicatore "nessun operatore" -->
          @if (!getColumnSelection(i)) {
            <div class="rdc-col-empty">
              <mat-icon aria-hidden="true" matTooltip="Nessun operatore selezionato"
                >person_off</mat-icon
              >
            </div>
          }

          <!-- Placeholder visivo durante il drag: posizionato al minuto
               esatto a cui si è snappato (v. dragState/DRAG_SNAP_MINUTES),
               non al solo slot da 30' che lo contiene. -->
          @if (ds && ds.columnIndex === i && ds.isColumnLocked) {
            <div class="rdc-drag-locked-hint">
              Non può essere spostato su un'altra colonna
            </div>
          }
          @if (ds && ds.columnIndex === i && ds.isValid) {
            <div
              class="rdc-drag-placeholder"
              [style.top.px]="getPlaceholderTopPx(ds.snappedMinutes)"
              [style.height.px]="PLACEHOLDER_DURATION_SLOTS * slotHeight"
            >
              <span class="rdc-drag-placeholder-time">{{
                getSnappedTimeLabel(ds.snappedMinutes)
              }}</span>
            </div>
          }

          <!-- Eventi -->
          @for (event of getEventsForColumn(i); track event.id) {
            @if (isClusterOverflowEvent(event)) {
              <button
                type="button"
                class="rdc-event-overflow"
                [style.top.px]="getOverflowTop(event)"
                [matMenuTriggerFor]="clusterMenu"
                (click)="$event.stopPropagation()"
              >
                {{ event.title }}
              </button>
              <mat-menu #clusterMenu="matMenu">
                @for (
                  extra of event.extendedProps['extraEvents'];
                  track extra.id
                ) {
                  <button
                    mat-menu-item
                    [style.border-left]="
                      '4px solid ' + (extra.borderColor || '#294A63')
                    "
                    (click)="eventClick.emit(extra)"
                  >
                    <span class="font-medium">{{ extra.title }}</span>
                    <span class="text-xs ml-2 opacity-70">{{
                      getEventTimeLabel(extra)
                    }}</span>
                  </button>
                }
              </mat-menu>
            } @else {
              <div
                class="rdc-event"
                [class.rdc-event--draggable]="canRescheduleEvent()(event)"
                [class.rdc-event--assenza]="isAssenzaEvent()(event)"
                [class.rdc-event--promemoria]="isPromemoriaCard(event)"
                [class.rdc-event--unresolved]="isAppuntamentoUnresolved(event)"
                [style.top.px]="getEventTop(event)"
                [style.height.px]="getEventHeight(event)"
                [style.left]="getEventLeftCss(event)"
                [style.width]="getEventWidthCss(event)"
                [style.right]="'auto'"
                [style.zIndex]="getEventZIndex(event)"
                [style.backgroundColor]="event.backgroundColor || ''"
                [style.borderColor]="event.borderColor || ''"
                [style.color]="event.textColor || ''"
                [matTooltip]="
                  getEventCardTitle(event) + '\n' + getEventTimeLabel(event)
                "
                [attr.draggable]="canRescheduleEvent()(event) ? 'true' : null"
                (dragstart)="
                  onEventDragStart($event, event, getColumnSelection(i)?.id)
                "
                (click)="eventClick.emit(event)"
                (keyup.enter)="eventClick.emit(event)"
                tabindex="0"
                role="button"
              >
                @if (isPromemoriaCard(event)) {
                  <span class="rdc-event-promemoria-title">{{
                    getEventCardTitle(event)
                  }}</span>
                  <span class="rdc-event-promemoria-deadline">{{
                    getPromemoriaDeadlineLabel(event)
                  }}</span>
                } @else {
                  <span class="rdc-event-title">{{
                    getEventCardTitle(event)
                  }}</span>
                  <span class="rdc-event-time">{{
                    getEventTimeLabel(event)
                  }}</span>
                }
              </div>
            }
          }
        </div>
      }

      <!-- Riga "adesso" -->
      @if (isToday()) {
        <div class="rdc-now-line" [style.top.px]="nowTop()"></div>
      }
    </div>
  </div>

  <!-- Quick-add menu, anchored to the clicked slot's cursor position. -->
  @if (quickAddMenuEnabled()) {
    <siat-quick-add-menu
      [options]="quickAddMenuOptions()"
      (optionSelected)="onQuickAddOptionClick($event)"
    />
  }
</div>
